import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #activeZoomContainer = null;
  #activeZoomImage = null;
  #lastMousePos = { x: 0, y: 0 };
  #hoverZoomController = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (window.matchMedia('(hover: hover)').matches) {
      this.#initHoverZoom();
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#hoverZoomController?.abort();
  }

  #initHoverZoom() {
    this.#hoverZoomController = new AbortController();
    const { signal } = this.#hoverZoomController;

    // Find all image containers (slides and grid items)
    const containers = this.querySelectorAll('.product-media-container--image .product-media, .product-media-container--zoomable .product-media');
    
    containers.forEach(container => {
      const img = container.querySelector('img.product-media__image, img');
      if (!img) return;

      // Ensure the container clips the zoomed image
      container.style.overflow = 'hidden';
      container.style.position = 'relative';

      container.addEventListener('mouseenter', (e) => {
        this.#activeZoomContainer = container;
        this.#activeZoomImage = img;
        this.#lastMousePos = { x: e.clientX, y: e.clientY };

        img.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform-origin 0.15s ease-out';
        img.style.transform = 'scale(2.2)'; // Amazon style 2.2x zoom
        
        // Calculate initial origin
        const rect = container.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
      }, { signal });

      container.addEventListener('mousemove', (e) => {
        if (this.#activeZoomImage !== img) return;
        this.#lastMousePos = { x: e.clientX, y: e.clientY };

        const rect = container.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        
        // Fast mouse movements should be extremely responsive
        img.style.transition = 'transform 0.1s ease-out, transform-origin 0.08s ease-out';
        img.style.transformOrigin = `${x}% ${y}%`;
      }, { signal });

      container.addEventListener('mouseleave', () => {
        if (this.#activeZoomImage === img) {
          this.#activeZoomContainer = null;
          this.#activeZoomImage = null;
        }
        
        img.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform-origin 0.25s ease-out';
        img.style.transform = 'scale(1)';
        img.style.transformOrigin = 'center center';
      }, { signal });
    });

    // Handle scrolling smoothly
    window.addEventListener('scroll', this.#handleScrollZoom, { passive: true, signal });
  }

  #handleScrollZoom = () => {
    if (!this.#activeZoomContainer || !this.#activeZoomImage) return;
    
    const container = this.#activeZoomContainer;
    const img = this.#activeZoomImage;
    const rect = container.getBoundingClientRect();
    
    // Recalculate relative position based on last screen mouse coordinates
    const x = ((this.#lastMousePos.x - rect.left) / rect.width) * 100;
    const y = ((this.#lastMousePos.y - rect.top) / rect.height) * 100;
    
    // Smooth transition during scrolling
    img.style.transition = 'transform-origin 0.15s ease-out';
    img.style.transformOrigin = `${x}% ${y}%`;
    
    // If the scroll pushes the container out of the mouse cursor, zoom out
    if (this.#lastMousePos.x < rect.left || this.#lastMousePos.x > rect.right ||
        this.#lastMousePos.y < rect.top || this.#lastMousePos.y > rect.bottom) {
      // Smooth reset transition
      img.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform-origin 0.25s ease-out';
      img.style.transform = 'scale(1)';
      img.style.transformOrigin = 'center center';
      this.#activeZoomContainer = null;
      this.#activeZoomImage = null;
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
